import { computed } from "@angular/core";
import { signalStore, withState, withComputed, withMethods, withProps, patchState } from "@ngrx/signals";
import { S25Util } from "../../util/s25-util";
import { s25Resource } from "../../util/s25.resource";
import { NameService } from "../../services/name.service";
import { Item } from "../../pojo/Item";
import type { Invoice, InvoiceNamesAndIds, LineItem, MicroBilling, Occurrence } from "../../pojo/Pricing";
import type { EventI } from "../../pojo/EventI";
import type { PaymentSummaryData } from "./s25-payments/s25.payments.component";
import type { IPaymentDetails } from "./s25-payments/payment.service";
import { S25PricingUtil } from "./s25.pricing.util";

interface S25PricingState {
    invoices: Invoice[];
    expandedInfo: MicroBilling["expandedInfo"] | null;
    occurrences: Occurrence[];
    locatorMap: Record<number, string>;
    invoiceSummaryData: InvoiceNamesAndIds[];
}

const initialState: S25PricingState = {
    invoices: [],
    expandedInfo: null,
    occurrences: [],
    locatorMap: {},
    invoiceSummaryData: [],
};

export const S25PricingStore = signalStore(
    withState(initialState),
    withComputed((store) => ({
        occMap: computed(
            () =>
                store.occurrences().reduce((map: Record<number, Occurrence>, occurrence: Occurrence) => {
                    map[occurrence.rsrvId] = occurrence;
                    return map;
                }, {}) ?? {},
        ),
        billNameMap: computed(() =>
            store.invoiceSummaryData().reduce(
                (map: Record<number | "eventType", Set<string>>, invoice) => {
                    [...invoice.rsrvIds].forEach((occId) => {
                        if (!map[occId]) map[occId] = new Set();
                        map[occId].add(invoice.name);
                    });

                    [...invoice.reqIds].forEach((reqId) => {
                        if (!map[reqId]) map[reqId] = new Set();
                        map[reqId].add(invoice.name);
                    });

                    if (invoice.includeEventType) map.eventType.add(invoice.name);
                    return map;
                },
                <Record<number | "eventType", Set<string>>>{ eventType: new Set() },
            ),
        ),
        paymentSummary: computed(() =>
            store.invoices().reduce((map: Record<number, PaymentSummaryData>, invoice: Invoice) => {
                invoice.payments?.forEach((payment: IPaymentDetails) => {
                    if (!map[payment.organizationId]) map[payment.organizationId] = [];
                    map[payment.organizationId].push({
                        ...payment,
                        invoiceId: invoice.id,
                        invoiceName: invoice.billDefn.billName,
                    });
                });
                return map;
            }, {}),
        ),
    })),
    withComputed((store) => ({
        orgs: computed(() => store.expandedInfo()?.organizations ?? []),
        missingOrgIds: computed(() => {
            const paymentOrgIds = Object.keys(store.paymentSummary()).map(Number);
            const knownOrgIds = new Set((store.expandedInfo()?.organizations ?? []).map((org) => org.organizationId));
            return paymentOrgIds.filter((orgId) => !knownOrgIds.has(orgId));
        }),
    })),
    withProps((store) => ({
        fetchedOrgNames: s25Resource({
            if: () => store.missingOrgIds().length > 0,
            params: () => store.missingOrgIds(),
            loader: async ({ params: orgIds }) => {
                const items = orgIds.map((orgId) => ({ itemId: orgId, itemTypeId: Item.Ids.Organization }));
                return NameService.getNames(items);
            },
        }),
    })),
    withComputed((store) => ({
        orgNameMap: computed(() => {
            const map: Record<number, string> = {};
            // Start with known orgs from expandedInfo
            for (const org of store.orgs()) {
                if (org.organizationId && org.organizationName) {
                    map[org.organizationId] = org.organizationName;
                }
            }
            // Merge fetched names for missing orgs
            const fetched = store.fetchedOrgNames.value();
            if (fetched) {
                for (const orgId of store.missingOrgIds()) {
                    const key = NameService.itemKeyFromIds(orgId, Item.Ids.Organization);
                    if (fetched[key]) map[orgId] = fetched[key];
                }
            }
            return map;
        }),
    })),
    withMethods((store) => ({
        setOccurrences(occurrences: Occurrence[]) {
            patchState(store, { occurrences: occurrences ?? [] });
        },

        setLocatorMap(events: EventI[]) {
            const locatorMap = events?.reduce((acc: { [key: number]: string }, event: EventI) => {
                acc[event.event_id] = event.event_locator;
                return acc;
            }, {});
            patchState(store, { locatorMap: locatorMap ?? {} });
        },

        setInvoiceSummaryData(data: InvoiceNamesAndIds[]) {
            patchState(store, { invoiceSummaryData: data });
        },

        setBillingData(billing: MicroBilling | null) {
            const invoices = (billing?.data?.items ?? []).filter(S25PricingUtil.notSystemInvoice);
            patchState(store, {
                invoices: S25Util.deepCopy(invoices),
                expandedInfo: billing?.expandedInfo ?? null,
            });
        },

        updateInvoiceName(invoiceId: number, newName: string) {
            patchState(store, {
                invoices: store.invoices().map((invoice) => {
                    if (invoice.id === invoiceId) {
                        return { ...invoice, billDefn: { ...invoice.billDefn, billName: newName } };
                    }
                    return invoice;
                }),
                invoiceSummaryData: store.invoiceSummaryData().map((invoice) => {
                    if (invoice.id === invoiceId) {
                        return { ...invoice, name: newName };
                    }
                    return invoice;
                }),
            });
        },

        updateLineItemAdjustment(lineItem: LineItem, evBillId: number) {
            patchState(store, {
                invoiceSummaryData: store.invoiceSummaryData().map((invoice) => {
                    if (invoice.id === evBillId) {
                        let itemFound; // flag to determine whether to update an existing adjustment or create a new one
                        const updatedLineItems = invoice.lineItemAdjustments.map((item) => {
                            if (item.itemId === lineItem.itemId && item.itemType === lineItem.itemType) {
                                itemFound = true;
                                return {
                                    ...item,
                                    ...(S25Util.isDefined(lineItem.adjustmentAmt)
                                        ? { adjustmentAmt: lineItem.adjustmentAmt }
                                        : { adjustmentPercent: lineItem.adjustmentPercent }),
                                };
                            }
                            return item;
                        });
                        return {
                            ...invoice,
                            lineItemAdjustments: itemFound
                                ? updatedLineItems
                                : [
                                      ...updatedLineItems,
                                      {
                                          adjustmentAmt: lineItem.adjustmentAmt,
                                          adjustmentPercent: lineItem.adjustmentPercent,
                                          itemName: lineItem.itemName,
                                          itemId: lineItem.itemId,
                                          itemType: lineItem.itemType,
                                          billId: invoice.id,
                                          invoiceName: invoice.name ?? "",
                                          chargeToId: lineItem.chargeToId,
                                          profileId: lineItem.profileId,
                                          adjustmentName: lineItem.adjustmentName,
                                          rsrvIds: new Set(lineItem.occurrences?.map((occ) => occ.rsrvId) ?? []),
                                          newAdjustments: [],
                                      },
                                  ],
                        };
                    }
                    return invoice;
                }),
            });
        },
    })),
);
